import React, { useEffect, useRef, useState } from 'react';
import { useBootContext, vortxTypography } from '@vortx/shared-ui';
import { useFrameCounter } from '../../hooks/useFrameCounter';
import { auth } from '../../config/firebase';
import { signInWithEmailAndPassword } from 'firebase/auth';

// ---------------------------------------------------------------------------
// Animation math helpers
// ---------------------------------------------------------------------------

function interpolate(value, inputRange, outputRange, options = {}) {
    const {
        extrapolateLeft = 'extend',
        extrapolateRight = 'extend',
        easing = null,
    } = options;

    // Find the segment the value falls into
    let i = 0;
    for (let k = 1; k < inputRange.length; k++) {
        if (value >= inputRange[k]) {
            i = k;
        } else {
            break;
        }
    }
    const segStart = Math.min(i, inputRange.length - 2);
    const segEnd = segStart + 1;

    const inMin = inputRange[segStart];
    const inMax = inputRange[segEnd];
    const outMin = outputRange[segStart];
    const outMax = outputRange[segEnd];

    // Compute raw t within this segment
    let t = inMax === inMin ? 1 : (value - inMin) / (inMax - inMin);

    // Clamp before easing if needed
    if (t < 0 && extrapolateLeft === 'clamp') t = 0;
    if (t > 1 && extrapolateRight === 'clamp') t = 1;

    // Apply easing only to the normalized 0-1 portion
    if (easing && t >= 0 && t <= 1) {
        t = easing(t);
    }

    const result = outMin + t * (outMax - outMin);

    // Final clamp pass for the overall output range
    if (extrapolateLeft === 'clamp' && value <= inputRange[0]) {
        return outputRange[0];
    }
    if (extrapolateRight === 'clamp' && value >= inputRange[inputRange.length - 1]) {
        return outputRange[outputRange.length - 1];
    }

    return result;
}

function spring({ frame: f, fps, config = {} }) {
    const {
        damping = 10,
        stiffness = 100,
        mass = 1,
        overshootClamping = false,
    } = config;

    // Simulate damped spring using step integration
    const stepPerFrame = 1;
    const steps = Math.max(0, Math.floor(f)) * stepPerFrame;
    const dt = 1 / (fps * stepPerFrame);

    let position = 0;
    let velocity = 0;
    const target = 1;

    for (let i = 0; i < steps; i++) {
        const springForce = -stiffness * (position - target);
        const dampingForce = -damping * velocity;
        const acceleration = (springForce + dampingForce) / mass;
        velocity += acceleration * dt;
        position += velocity * dt;

        if (overshootClamping && position > target) {
            position = target;
            velocity = 0;
        }
    }

    return Math.max(0, Math.min(1, position));
}

const easeInOutQuad = (t) => t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
const easeOutQuad = (t) => 1 - (1 - t) * (1 - t);
const easeInQuad = (t) => t * t;

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const FPS = 30;
const MIN_BOOT_FRAMES = 3.5 * FPS; // 105 frames
const ICON_WIDTH = 160;
const ICON_HEIGHT = Math.round(ICON_WIDTH * (867 / 1024));
const LOGO_DISPLAY_HEIGHT = 80;
const TEXT_WIDTH = Math.round((2928 / 656) * LOGO_DISPLAY_HEIGHT);

const BOOT_LINES = [
    { text: "DEPT. OF ENERGY // SECURE TERMINAL v4.7.2", delay: 0 },
    { text: "INITIALIZING SUBSYSTEM..........OK", delay: 4 },
    { text: "REACTOR CORE DIAGNOSTICS.......OK", delay: 9 },
    { text: "CLEARANCE: \u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588 LEVEL 5", delay: 14 },
    { text: "LOADING ASSET: VORTX//FLUX", delay: 20 },
    { text: "> ACCESS GRANTED", delay: 26 },
];

const WAIT_LINES = [
    "INITIALIZING SUBSYSTEMS...",
    "CALIBRATING SENSORS...",
    "SYNCHRONIZING DATA FEEDS...",
];

// Pre-compute particle definitions (deterministic)
const PARTICLES = Array.from({ length: 22 }, (_, i) => ({
    angle:
        (i / 22) * Math.PI * 2 +
        (((i * 137.5 * Math.PI) / 180) % (Math.PI * 2)),
    speed: i % 3 === 0 ? 200 + (i % 7) * 40 : 80 + (i % 5) * 25,
    size: i % 3 === 0 ? 1.5 : 2.5 + (i % 3),
    delay: (i % 5) * 2,
    trail: i % 3 === 0,
    color: i % 2 === 0 ? 'gold' : 'teal',
}));

const PARTICLE_COLORS = {
    gold: { r: 178, g: 140, b: 80 },
    teal: { r: 95, g: 212, b: 196 },
};

// ---------------------------------------------------------------------------
// Sub-components (each receives { frame, fps })
// ---------------------------------------------------------------------------

function RetroGridBG({ frame }) {
    const scrollY = frame * 0.8;

    return (
        <div
            style={{
                position: 'absolute',
                inset: 0,
                perspective: 600,
                overflow: 'hidden',
                pointerEvents: 'none',
            }}
        >
            <div
                style={{
                    position: 'absolute',
                    left: '-50%',
                    top: '-20%',
                    width: '200%',
                    height: '200%',
                    backgroundImage: `
                        linear-gradient(rgba(63, 106, 100, 0.08) 1px, transparent 1px),
                        linear-gradient(90deg, rgba(63, 106, 100, 0.08) 1px, transparent 1px)
                    `,
                    backgroundSize: '60px 60px',
                    backgroundPosition: `0 ${scrollY}px`,
                    transform: 'rotateX(60deg)',
                    transformOrigin: 'center center',
                }}
            />
        </div>
    );
}

function BootText({ frame, fps, isFullyReady }) {
    // The whole boot text block fades out as the logo appears
    const fadeOut = interpolate(frame, [2.0 * fps, 2.6 * fps], [1, 0], {
        extrapolateLeft: 'clamp',
        extrapolateRight: 'clamp',
        easing: easeInQuad,
    });

    // After MIN_BOOT_FRAMES, if not fully ready, show wait lines to fill time
    const showWait = frame > MIN_BOOT_FRAMES && !isFullyReady;
    const waitLineStartFrame = MIN_BOOT_FRAMES;

    return (
        <div
            style={{
                position: 'absolute',
                left: 80,
                top: 60,
                opacity: showWait ? 1 : fadeOut,
                pointerEvents: 'none',
            }}
        >
            {BOOT_LINES.map((line, i) => {
                const charCount = line.text.length;
                const startFrame = line.delay;
                const charsVisible = Math.floor(
                    interpolate(frame, [startFrame, startFrame + charCount * 0.6], [0, charCount], {
                        extrapolateLeft: 'clamp',
                        extrapolateRight: 'clamp',
                    })
                );

                const isAccessLine = line.text.includes('ACCESS GRANTED');
                const textColor = isAccessLine ? '#5fd4c4' : '#b28c50';

                // Blinking cursor at end of current line
                const isCurrentLine =
                    frame >= startFrame &&
                    (i === BOOT_LINES.length - 1 || frame < BOOT_LINES[i + 1].delay);
                const cursorVisible =
                    isCurrentLine && charsVisible < charCount && Math.floor(frame * 0.15) % 2 === 0;

                if (charsVisible <= 0) return null;

                return (
                    <div
                        key={`boot-${i}`}
                        style={{
                            fontFamily: vortxTypography.mono,
                            fontSize: 13,
                            color: textColor,
                            letterSpacing: 1,
                            lineHeight: 1.8,
                            textShadow: `0 0 6px ${isAccessLine ? 'rgba(95, 212, 196, 0.5)' : 'rgba(178, 140, 80, 0.4)'}`,
                            whiteSpace: 'pre',
                        }}
                    >
                        {line.text.slice(0, charsVisible)}
                        {cursorVisible && (
                            <span style={{ color: textColor, opacity: 0.8 }}>{'\u2588'}</span>
                        )}
                    </div>
                );
            })}

            {/* Wait lines displayed when we exceed MIN_BOOT_FRAMES and app is not ready */}
            {showWait && WAIT_LINES.map((wline, wi) => {
                const wStart = waitLineStartFrame + wi * 20;
                if (frame < wStart) return null;

                const charCount = wline.length;
                const charsVisible = Math.floor(
                    interpolate(frame, [wStart, wStart + charCount * 0.6], [0, charCount], {
                        extrapolateLeft: 'clamp',
                        extrapolateRight: 'clamp',
                    })
                );

                // Cycle the dots animation for an indefinite loading feel
                const dotBase = wline.replace(/\.+$/, '');
                const dotCount = wline.length - dotBase.length;
                let displayText;
                if (dotCount > 0 && charsVisible >= charCount) {
                    const cycle = Math.floor((frame - wStart) * 0.1) % (dotCount + 1);
                    displayText = dotBase + '.'.repeat(cycle);
                } else {
                    displayText = wline.slice(0, charsVisible);
                }

                const cursorOn =
                    charsVisible < charCount && Math.floor(frame * 0.15) % 2 === 0;

                return (
                    <div
                        key={`wait-${wi}`}
                        style={{
                            fontFamily: vortxTypography.mono,
                            fontSize: 13,
                            color: '#b28c50',
                            letterSpacing: 1,
                            lineHeight: 1.8,
                            textShadow: '0 0 6px rgba(178, 140, 80, 0.4)',
                            whiteSpace: 'pre',
                        }}
                    >
                        {displayText}
                        {cursorOn && (
                            <span style={{ color: '#b28c50', opacity: 0.8 }}>{'\u2588'}</span>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

function ScanBeam({ frame, fps }) {
    // Primary scan line sweeps top-to-bottom
    const scanY = interpolate(frame, [0, 1.2 * fps], [-5, 105], {
        extrapolateLeft: 'clamp',
        extrapolateRight: 'clamp',
        easing: easeInOutQuad,
    });

    const scanOpacity = interpolate(
        frame,
        [0, 0.1 * fps, 1.0 * fps, 1.2 * fps],
        [0, 1, 1, 0],
        { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
    );

    // Second pass: thinner, faster
    const scan2Y = interpolate(frame, [0.4 * fps, 1.0 * fps], [-5, 105], {
        extrapolateLeft: 'clamp',
        extrapolateRight: 'clamp',
        easing: easeOutQuad,
    });
    const scan2Opacity = interpolate(
        frame,
        [0.4 * fps, 0.5 * fps, 0.9 * fps, 1.0 * fps],
        [0, 0.6, 0.6, 0],
        { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
    );

    return (
        <>
            {/* Primary scan beam */}
            <div
                style={{
                    position: 'absolute',
                    left: 0,
                    top: `${scanY}%`,
                    width: '100%',
                    height: 3,
                    background:
                        'linear-gradient(180deg, transparent, rgba(95, 212, 196, 0.8), rgba(95, 212, 196, 0.3), transparent)',
                    boxShadow:
                        '0 0 30px 10px rgba(95, 212, 196, 0.2), 0 0 60px 20px rgba(95, 212, 196, 0.1)',
                    opacity: scanOpacity,
                    pointerEvents: 'none',
                }}
            />

            {/* Secondary thinner scan */}
            <div
                style={{
                    position: 'absolute',
                    left: 0,
                    top: `${scan2Y}%`,
                    width: '100%',
                    height: 1,
                    background: 'rgba(178, 140, 80, 0.6)',
                    boxShadow: '0 0 15px 5px rgba(178, 140, 80, 0.15)',
                    opacity: scan2Opacity,
                    pointerEvents: 'none',
                }}
            />
        </>
    );
}

function EnergyRings({ frame, fps }) {
    // Primary teal ring
    const ringScale = interpolate(frame, [0, 1.2 * fps], [0.2, 3.5], {
        extrapolateRight: 'clamp',
        easing: easeOutQuad,
    });
    const ringOpacity = interpolate(
        frame,
        [0, 0.3 * fps, 1.2 * fps],
        [0, 0.6, 0],
        { extrapolateRight: 'clamp' }
    );

    // Secondary gold ring, delayed
    const ring2Start = 6;
    const ring2Scale = interpolate(
        frame,
        [ring2Start, ring2Start + 1.2 * fps],
        [0.2, 2.8],
        { extrapolateLeft: 'clamp', extrapolateRight: 'clamp', easing: easeOutQuad }
    );
    const ring2Opacity = interpolate(
        frame,
        [ring2Start, ring2Start + 0.2 * fps, ring2Start + 1.0 * fps],
        [0, 0.35, 0],
        { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
    );

    return (
        <>
            {/* Teal ring */}
            <div
                style={{
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    width: 120,
                    height: 120,
                    borderRadius: '50%',
                    border: '1.5px solid rgba(95, 212, 196, 0.8)',
                    boxShadow:
                        '0 0 20px 4px rgba(95, 212, 196, 0.3), inset 0 0 10px rgba(95, 212, 196, 0.1)',
                    transform: `translate(-50%, -50%) scale(${ringScale})`,
                    opacity: ringOpacity,
                    pointerEvents: 'none',
                }}
            />
            {/* Gold ring */}
            <div
                style={{
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    width: 120,
                    height: 120,
                    borderRadius: '50%',
                    border: '1px solid rgba(178, 140, 80, 0.6)',
                    boxShadow: '0 0 12px 2px rgba(178, 140, 80, 0.25)',
                    transform: `translate(-50%, -50%) scale(${ring2Scale})`,
                    opacity: ring2Opacity,
                    pointerEvents: 'none',
                }}
            />
        </>
    );
}

function ParticlesBurst({ frame, fps }) {
    return (
        <div style={{ position: 'absolute', inset: 0 }}>
            {PARTICLES.map((p, i) => {
                const localFrame = Math.max(0, frame - p.delay);
                const progress = interpolate(localFrame, [0, 1.5 * fps], [0, 1], {
                    extrapolateRight: 'clamp',
                    easing: easeOutQuad,
                });

                const distance = progress * p.speed;
                const x = Math.cos(p.angle) * distance;
                const y = Math.sin(p.angle) * distance;

                const opacity = interpolate(
                    progress,
                    [0, 0.05, 0.4, 0.8, 1],
                    [0, 1, 0.7, 0.2, 0],
                    { extrapolateRight: 'clamp' }
                );

                const c = PARTICLE_COLORS[p.color];
                const rgba = (a) => `rgba(${c.r}, ${c.g}, ${c.b}, ${a})`;

                if (p.trail) {
                    // Streak / trail particle
                    const trailLength = 15 + progress * 25;
                    const angleDeg = (p.angle * 180) / Math.PI;
                    return (
                        <div
                            key={i}
                            style={{
                                position: 'absolute',
                                left: '50%',
                                top: '50%',
                                width: trailLength,
                                height: 1.5,
                                borderRadius: 1,
                                background: `linear-gradient(90deg, transparent, ${rgba(opacity)})`,
                                transform: `translate(${x}px, ${y}px) rotate(${angleDeg}deg)`,
                                transformOrigin: '0% 50%',
                                opacity,
                            }}
                        />
                    );
                }

                // Dot particle
                return (
                    <div
                        key={i}
                        style={{
                            position: 'absolute',
                            left: '50%',
                            top: '50%',
                            width: p.size,
                            height: p.size,
                            borderRadius: '50%',
                            backgroundColor: rgba(1),
                            boxShadow: `0 0 ${p.size * 3}px ${p.size}px ${rgba(0.5)}`,
                            transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`,
                            opacity,
                        }}
                    />
                );
            })}
        </div>
    );
}

function VortxIconElement({ frame, fps }) {
    // Phase 1: Icon materializes via vertical clip reveal (scanned in)
    const revealProgress = interpolate(frame, [0, 1.2 * fps], [0, 100], {
        extrapolateLeft: 'clamp',
        extrapolateRight: 'clamp',
        easing: easeInOutQuad,
    });

    // Phase 2: Glitch/flicker as it locks in
    const glitchZone = frame > 0.8 * fps && frame < 1.4 * fps;
    const glitchOffset = glitchZone
        ? Math.sin(frame * 3.7) * interpolate(frame, [0.8 * fps, 1.4 * fps], [3, 0], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
        })
        : 0;

    // Phase 3: After materialization, subtle breathing glow
    const settled = interpolate(frame, [1.4 * fps, 1.6 * fps], [0, 1], {
        extrapolateLeft: 'clamp',
        extrapolateRight: 'clamp',
    });
    const pulsePhase = Math.max(0, frame - 1.6 * fps);
    const pulse = 1 + Math.sin(pulsePhase * 0.12) * 0.02 * settled;

    // Gold phosphor glow intensifies as icon locks in
    const glowIntensity = interpolate(
        frame,
        [0.6 * fps, 1.2 * fps, 2.0 * fps, 3.5 * fps],
        [0, 0.9, 0.5, 0.2],
        { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
    );

    const opacity = interpolate(frame, [0, 6], [0, 1], {
        extrapolateRight: 'clamp',
    });

    return (
        <div
            style={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                width: ICON_WIDTH,
                height: ICON_HEIGHT,
                transform: `translate(-50%, -50%) scale(${pulse}) translateX(${glitchOffset}px)`,
                opacity,
            }}
        >
            {/* Phosphor glow */}
            <div
                style={{
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    width: ICON_WIDTH + 60 + glowIntensity * 80,
                    height: ICON_HEIGHT + 60 + glowIntensity * 80,
                    borderRadius: '50%',
                    background: `radial-gradient(circle, rgba(178, 140, 80, ${glowIntensity * 0.4}) 0%, rgba(95, 212, 196, ${glowIntensity * 0.08}) 50%, transparent 70%)`,
                    transform: 'translate(-50%, -50%)',
                    pointerEvents: 'none',
                }}
            />

            {/* Vertical clip reveal: icon scans in top-to-bottom */}
            <div
                style={{
                    clipPath: `inset(0 0 ${100 - revealProgress}% 0)`,
                    position: 'relative',
                }}
            >
                <img
                    src="/assets/vortx-icon.png"
                    alt=""
                    style={{
                        width: ICON_WIDTH,
                        height: ICON_HEIGHT,
                        objectFit: 'contain',
                    }}
                />
            </div>

            {/* Scan edge glow at the reveal frontier */}
            {revealProgress < 100 && revealProgress > 2 && (
                <div
                    style={{
                        position: 'absolute',
                        left: 0,
                        top: `${revealProgress}%`,
                        width: '100%',
                        height: 2,
                        background: 'rgba(95, 212, 196, 0.7)',
                        boxShadow:
                            '0 0 12px 4px rgba(95, 212, 196, 0.3), 0 -4px 20px rgba(95, 212, 196, 0.15)',
                        transform: 'translateY(-50%)',
                    }}
                />
            )}
        </div>
    );
}

function VortxTextElement({ frame, fps }) {
    // Clip reveal: text masked, reveals left-to-right using spring
    const revealProgress = spring({
        frame,
        fps,
        config: { damping: 20, stiffness: 120 },
    });
    const clipRight = interpolate(revealProgress, [0, 1], [100, 0]);

    // Subtle slide from right
    const slideX = interpolate(revealProgress, [0, 1], [40, 0]);

    const opacity = interpolate(frame, [0, 6], [0, 1], {
        extrapolateRight: 'clamp',
    });

    // Phosphor glow sweep after reveal
    const sweepStart = 1.2 * fps;
    const sweepX = interpolate(
        frame,
        [sweepStart, sweepStart + 0.5 * fps],
        [-20, 120],
        {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
            easing: easeInOutQuad,
        }
    );

    return (
        <div
            style={{
                position: 'relative',
                width: TEXT_WIDTH,
                height: LOGO_DISPLAY_HEIGHT,
                overflow: 'hidden',
                opacity,
                transform: `translateX(${slideX}px)`,
                filter: 'drop-shadow(0 0 8px rgba(95, 212, 196, 0.3))',
            }}
        >
            <div
                style={{
                    width: '100%',
                    height: '100%',
                    clipPath: `inset(0 ${clipRight}% 0 0)`,
                }}
            >
                <img
                    src="/assets/vortx-name-only.png"
                    alt=""
                    style={{
                        height: LOGO_DISPLAY_HEIGHT,
                        width: TEXT_WIDTH,
                        objectFit: 'contain',
                    }}
                />
            </div>

            {/* Teal phosphor sweep */}
            <div
                style={{
                    position: 'absolute',
                    top: 0,
                    left: `${sweepX}%`,
                    width: 50,
                    height: '100%',
                    background:
                        'linear-gradient(90deg, transparent, rgba(95, 212, 196, 0.25), transparent)',
                    pointerEvents: 'none',
                    filter: 'blur(6px)',
                }}
            />
        </div>
    );
}

function LogoRevealSection({ frame, fps }) {
    // Icon shifts left to make room for text
    const shiftStart = 1.8 * fps;
    const shiftEnd = 2.5 * fps;
    const shift = interpolate(frame, [shiftStart, shiftEnd], [0, -120], {
        extrapolateLeft: 'clamp',
        extrapolateRight: 'clamp',
        easing: easeInOutQuad,
    });

    // Text appears at ~2.2s (frame 66 at 30fps)
    const textStart = Math.round(2.2 * fps);
    const showText = frame >= textStart;

    return (
        <>
            {/* Icon scans in from frame 0, shifts left for text */}
            <div
                style={{
                    position: 'absolute',
                    left: `calc(50% + ${shift}px)`,
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                }}
            >
                <VortxIconElement frame={frame} fps={fps} />
            </div>

            {/* Text reveals at ~2.2s */}
            {showText && (
                <div
                    style={{
                        position: 'absolute',
                        left: 'calc(50% - 20px)',
                        top: '50%',
                        transform: 'translateY(-50%)',
                    }}
                >
                    <VortxTextElement frame={frame - textStart} fps={fps} />
                </div>
            )}
        </>
    );
}

function ClassBar({ frame, fps }) {
    const opacity = interpolate(frame, [0, 0.4 * fps], [0, 0.5], {
        extrapolateRight: 'clamp',
    });

    return (
        <div
            style={{
                position: 'absolute',
                bottom: 30,
                left: 0,
                right: 0,
                display: 'flex',
                justifyContent: 'center',
                opacity,
                pointerEvents: 'none',
            }}
        >
            <div
                style={{
                    fontFamily: vortxTypography.mono,
                    fontSize: 10,
                    color: '#b28c50',
                    letterSpacing: 4,
                    textTransform: 'uppercase',
                    textShadow: '0 0 4px rgba(178, 140, 80, 0.3)',
                }}
            >
                {'\u2588\u2588'} CLASSIFIED // DEPT. OF ENERGY // PROJECT VORTX {'\u2588\u2588'}
            </div>
        </div>
    );
}

function CRTOverlay({ frame, fps }) {
    // CRT flicker: subtle opacity dips
    const flickerCycle = (frame / fps) * Math.PI * 2 * 0.125; // ~8s cycle
    const flicker = 0.93 + Math.sin(flickerCycle) * 0.02;

    // Scanline scroll offset
    const scrollOffset = (frame * 0.5) % 4;

    return (
        <div
            style={{
                position: 'absolute',
                inset: 0,
                opacity: flicker,
                pointerEvents: 'none',
                mixBlendMode: 'multiply',
            }}
        >
            {/* Scanlines */}
            <div
                style={{
                    position: 'absolute',
                    inset: 0,
                    backgroundImage: `repeating-linear-gradient(
                        0deg,
                        rgba(0, 0, 0, 0.08) 0px,
                        rgba(0, 0, 0, 0.08) 1px,
                        transparent 1px,
                        transparent 3px
                    )`,
                    backgroundPosition: `0 ${scrollOffset}px`,
                }}
            />

            {/* Vignette */}
            <div
                style={{
                    position: 'absolute',
                    inset: 0,
                    background:
                        'radial-gradient(ellipse at center, transparent 50%, rgba(0, 0, 0, 0.5) 100%)',
                }}
            />
        </div>
    );
}

// ---------------------------------------------------------------------------
// Boot Login Form
// ---------------------------------------------------------------------------

function BootLoginForm({ fadeIn }) {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    async function handleSubmit(e) {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            await signInWithEmailAndPassword(auth, email, password);
            // AuthContext picks up state change → AuthReadyReporter fires → Phase 4
        } catch (err) {
            const code = err.code || '';
            if (code === 'auth/invalid-credential' || code === 'auth/wrong-password' || code === 'auth/user-not-found') {
                setError('INVALID CREDENTIALS');
            } else if (code === 'auth/too-many-requests') {
                setError('TOO MANY ATTEMPTS — TRY LATER');
            } else {
                setError('AUTHENTICATION FAILED');
            }
            setLoading(false);
        }
    }

    const inputStyle = {
        width: '100%',
        padding: '10px 12px',
        fontFamily: vortxTypography.mono,
        fontSize: 13,
        color: '#5fd4c4',
        backgroundColor: 'rgba(10, 18, 16, 0.9)',
        border: '1px solid rgba(95, 212, 196, 0.3)',
        borderRadius: 2,
        outline: 'none',
        letterSpacing: 1,
        boxSizing: 'border-box',
    };

    return (
        <div
            style={{
                position: 'absolute',
                left: '50%',
                top: '62%',
                transform: 'translateX(-50%)',
                width: 320,
                opacity: fadeIn,
                transition: 'opacity 0.8s ease',
                pointerEvents: fadeIn > 0.1 ? 'auto' : 'none',
            }}
        >
            <form onSubmit={handleSubmit} autoComplete="off">
                <div style={{
                    fontFamily: vortxTypography.mono,
                    fontSize: 10,
                    color: '#b28c50',
                    letterSpacing: 3,
                    textTransform: 'uppercase',
                    marginBottom: 16,
                    textAlign: 'center',
                    textShadow: '0 0 4px rgba(178, 140, 80, 0.3)',
                }}>
                    {'\u2588'} OPERATOR AUTHENTICATION REQUIRED {'\u2588'}
                </div>

                <div style={{ marginBottom: 12 }}>
                    <label style={{
                        fontFamily: vortxTypography.mono,
                        fontSize: 10,
                        color: 'rgba(95, 212, 196, 0.6)',
                        letterSpacing: 2,
                        display: 'block',
                        marginBottom: 4,
                    }}>
                        IDENT
                    </label>
                    <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        style={inputStyle}
                        autoComplete="email"
                        required
                        autoFocus
                    />
                </div>

                <div style={{ marginBottom: 16 }}>
                    <label style={{
                        fontFamily: vortxTypography.mono,
                        fontSize: 10,
                        color: 'rgba(95, 212, 196, 0.6)',
                        letterSpacing: 2,
                        display: 'block',
                        marginBottom: 4,
                    }}>
                        PASSKEY
                    </label>
                    <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        style={inputStyle}
                        autoComplete="current-password"
                        required
                    />
                </div>

                {error && (
                    <div style={{
                        fontFamily: vortxTypography.mono,
                        fontSize: 11,
                        color: '#ff6b6b',
                        textAlign: 'center',
                        marginBottom: 12,
                        textShadow: '0 0 6px rgba(255, 107, 107, 0.4)',
                        letterSpacing: 1,
                    }}>
                        {'> '}{error}
                    </div>
                )}

                <button
                    type="submit"
                    disabled={loading}
                    style={{
                        width: '100%',
                        padding: '10px 16px',
                        fontFamily: vortxTypography.mono,
                        fontSize: 12,
                        fontWeight: 700,
                        color: '#0a1210',
                        backgroundColor: loading ? 'rgba(178, 140, 80, 0.4)' : '#b28c50',
                        border: '1px solid #b28c50',
                        borderRadius: 2,
                        cursor: loading ? 'wait' : 'pointer',
                        letterSpacing: 3,
                        textTransform: 'uppercase',
                        textShadow: 'none',
                        boxShadow: loading ? 'none' : '0 0 12px rgba(178, 140, 80, 0.3)',
                    }}
                >
                    {loading ? 'AUTHENTICATING...' : 'AUTHENTICATE'}
                </button>
            </form>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const PANEL_ORDER = ['header', 'left', 'content', 'right', 'footer'];
const PANEL_STAGGER = 6;   // frames between each panel start
const PANEL_RAMP = 15;     // frames for full power-up ramp
const FLICKER_FRAME = 7;   // frame within ramp where flicker dip happens

export default function RetroBoot() {
    const { bootActive, isFullyReady, authResolved, onBootComplete, setPanelPower } = useBootContext();
    const { frame, fps } = useFrameCounter(bootActive);
    const powerStartRef = useRef(null);
    const completedRef = useRef(false);
    const loginHoldStartRef = useRef(null);

    // Show login form when boot animation is done, auth has resolved, but user isn't logged in
    const showLoginForm = frame >= MIN_BOOT_FRAMES && authResolved && !isFullyReady;

    // Track when login hold starts for fade-in timing
    if (showLoginForm && loginHoldStartRef.current === null) {
        loginHoldStartRef.current = frame;
    }
    if (!showLoginForm) {
        loginHoldStartRef.current = null;
    }

    // Phase 4: panel power-on with staggered timing
    useEffect(() => {
        if (!bootActive) return;
        if (!isFullyReady) return;
        if (frame < MIN_BOOT_FRAMES) return;

        // Mark the frame at which power-on starts
        if (powerStartRef.current === null) {
            powerStartRef.current = frame;
        }

        const elapsed = frame - powerStartRef.current;

        const newPower = {};
        let allDone = true;

        PANEL_ORDER.forEach((panel, idx) => {
            const panelStart = idx * PANEL_STAGGER;
            const localFrame = elapsed - panelStart;

            if (localFrame <= 0) {
                newPower[panel] = 0;
                allDone = false;
            } else if (localFrame >= PANEL_RAMP) {
                newPower[panel] = 1;
            } else {
                // Ramp up with a flicker dip in the middle
                let t = localFrame / PANEL_RAMP;
                // Create a flicker dip around the midpoint
                if (Math.abs(localFrame - FLICKER_FRAME) <= 1) {
                    t = t * 0.4; // brightness dip
                }
                newPower[panel] = Math.min(1, Math.max(0, t));
                allDone = false;
            }
        });

        setPanelPower(newPower);

        if (allDone && !completedRef.current) {
            completedRef.current = true;
            // Short delay so final power state renders before overlay disappears
            setTimeout(() => {
                onBootComplete();
            }, 100);
        }
    }, [bootActive, isFullyReady, frame, setPanelPower, onBootComplete]);

    if (!bootActive) return null;

    // Overlay fade-out during power-on phase
    let overlayOpacity = 1;
    if (powerStartRef.current !== null) {
        const elapsed = frame - powerStartRef.current;
        const totalPowerDuration = (PANEL_ORDER.length - 1) * PANEL_STAGGER + PANEL_RAMP;
        overlayOpacity = interpolate(elapsed, [0, totalPowerDuration], [1, 0], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
        });
    }

    // Sequence timing (matching LogoReveal.tsx)
    const scanBeamStart = Math.round(0.3 * fps);
    const energyRingStart = Math.round(1.2 * fps);
    const particlesStart = Math.round(0.8 * fps);
    const particlesEnd = particlesStart + Math.round(3 * fps);
    const classBarStart = Math.round(3.0 * fps);

    return (
        <div
            style={{
                position: 'fixed',
                inset: 0,
                zIndex: 9999,
                background: '#0a1210',
                opacity: overlayOpacity,
                pointerEvents: overlayOpacity > 0 ? 'auto' : 'none',
            }}
        >
            {/* Retro perspective grid */}
            <RetroGridBG frame={frame} />

            {/* Subtle ambient glow */}
            <div
                style={{
                    position: 'absolute',
                    inset: 0,
                    background:
                        'radial-gradient(ellipse at center, rgba(63, 106, 100, 0.06) 0%, transparent 60%)',
                }}
            />

            {/* Boot sequence text in top-left corner */}
            <BootText frame={frame} fps={fps} isFullyReady={isFullyReady} />

            {/* Full-screen scan line sweep as icon materializes */}
            {frame >= scanBeamStart && (
                <ScanBeam frame={frame - scanBeamStart} fps={fps} />
            )}

            {/* Energy rings on icon lock-in */}
            {frame >= energyRingStart && (
                <EnergyRings frame={frame - energyRingStart} fps={fps} />
            )}

            {/* Particles burst outward */}
            {frame >= particlesStart && frame < particlesEnd && (
                <ParticlesBurst frame={frame - particlesStart} fps={fps} />
            )}

            {/* Icon + text logo reveal — shifts up and pulses when login form is shown */}
            {(() => {
                let logoTranslateY = 0;
                let logoScale = 1;
                let logoGlow = 0;

                if (showLoginForm && loginHoldStartRef.current !== null) {
                    const holdElapsed = frame - loginHoldStartRef.current;
                    // Shift logo up over ~0.8s (24 frames at 30fps)
                    logoTranslateY = interpolate(holdElapsed, [0, 24], [0, -15], {
                        extrapolateLeft: 'clamp',
                        extrapolateRight: 'clamp',
                        easing: easeInOutQuad,
                    });
                    // Slow sine pulse on scale (±1.5%)
                    const pulseT = Math.max(0, holdElapsed - 10);
                    logoScale = 1 + Math.sin(pulseT * 0.08) * 0.015;
                    logoGlow = 0.15 + Math.sin(pulseT * 0.08) * 0.1;
                }

                return (
                    <div style={{
                        position: 'absolute',
                        inset: 0,
                        transform: `translateY(${logoTranslateY}vh) scale(${logoScale})`,
                        transition: showLoginForm ? 'none' : undefined,
                        filter: logoGlow > 0 ? `drop-shadow(0 0 ${logoGlow * 40}px rgba(95, 212, 196, ${logoGlow}))` : undefined,
                    }}>
                        <LogoRevealSection frame={frame} fps={fps} />
                    </div>
                );
            })()}

            {/* Login form — fades in below the logo when auth resolved but not logged in */}
            {showLoginForm && (
                <BootLoginForm
                    fadeIn={loginHoldStartRef.current !== null
                        ? interpolate(frame - loginHoldStartRef.current, [8, 30], [0, 1], {
                            extrapolateLeft: 'clamp',
                            extrapolateRight: 'clamp',
                        })
                        : 0
                    }
                />
            )}

            {/* Classification bar at bottom */}
            {frame >= classBarStart && (
                <ClassBar frame={frame - classBarStart} fps={fps} />
            )}

            {/* CRT scanlines + vignette always on top */}
            <CRTOverlay frame={frame} fps={fps} />
        </div>
    );
}
